/**
 * פרופיל אתר (D11) — פרטים שממלאים פעם אחת ומשמשים תנאי תחולה לצ׳קליסט.
 *
 * כלל היסוד: **ערך חסר הוא ״נדרש בירור״, לעולם לא ״לא״.** אם לא נרשם מינוי
 * של מנהל אתר, אין להסיק שאין מנהל אתר — רק שלא ידוע. לכן כל עובדה כאן היא
 * תלת־ערכית, ומנוע הצ׳קליסט (שלב 4) יציג שאלה שתנאיה ״לא ידוע״ עם הסבר,
 * במקום להסתיר אותה.
 */

import { SITE_MANAGER_AREA_THRESHOLD_SQM } from './checklist.ts';

export type Tri = 'yes' | 'no' | 'unknown';

export type SafetyPlanKind = 'management' | 'organization';

/**
 * סוג שלב בנייה (CQ-07, תוכן 0.2 — 06.10.2026). שם השלב נשאר טקסט חופשי (A-31); הסוג
 * משמש רק לכלל התחולה `stage_kind_any` — כלל מוצר, לא טענה משפטית. שלבים קיימים ← `other`.
 */
export type StageKind = 'excavation' | 'foundations' | 'skeleton' | 'finishing' | 'other';

export const STAGE_KINDS: StageKind[] = [
  'excavation',
  'foundations',
  'skeleton',
  'finishing',
  'other',
];

export const STAGE_KIND_LABEL: Record<StageKind, string> = {
  excavation: 'חפירות ודיפון',
  foundations: 'ביסוס ויסודות',
  skeleton: 'שלד',
  finishing: 'גמר וחזיתות',
  other: 'אחר',
};

/** ערך מהמסד; ערך לא מוכר (או חסר, לפני המיגרציה) ← ״אחר״. */
export function parseStageKind(raw: unknown): StageKind {
  return STAGE_KINDS.includes(raw as StageKind) ? (raw as StageKind) : 'other';
}

/** השדות כפי שהם ב־`public.site_profile`; null = נדרש בירור. */
export interface SiteProfile {
  permitAreaSqm: number | null;
  peakWorkerCount: number | null;
  safetyPlanKind: SafetyPlanKind | null;
  safetyPlanPreparedBy: string | null;
  safetyPlanUpdatedOn: string | null; // YYYY-MM-DD
  requiresSection192Notice: boolean | null;
  developerName: string | null;
  mainContractorName: string | null;
  /** האם מועסקים באתר עובדים זרים (תוכן 0.2, C6-05). null = נדרש בירור. */
  employsForeignWorkers: boolean | null;
}

export type AppointmentRole =
  'work_manager' | 'site_manager' | 'safety_inspector' | 'safety_officer' | 'other';

export interface ProfessionalAppointment {
  role: AppointmentRole;
  personName: string;
  startsOn: string | null; // YYYY-MM-DD; null = לא ידוע
  endsOn: string | null;
}

export const APPOINTMENT_ROLE_LABEL: Record<AppointmentRole, string> = {
  work_manager: 'מנהל עבודה',
  site_manager: 'מנהל אתר',
  safety_inspector: 'בקר בטיחות',
  safety_officer: 'ממונה בטיחות',
  other: 'תפקיד אחר',
};

export const SAFETY_PLAN_KIND_LABEL: Record<SafetyPlanKind, string> = {
  management: 'תוכנית לניהול הבטיחות',
  organization: 'תוכנית ארגון בטיחות',
};

export const EMPTY_SITE_PROFILE: SiteProfile = {
  permitAreaSqm: null,
  peakWorkerCount: null,
  safetyPlanKind: null,
  safetyPlanPreparedBy: null,
  safetyPlanUpdatedOn: null,
  requiresSection192Notice: null,
  developerName: null,
  mainContractorName: null,
  employsForeignWorkers: null,
};

/** תוויות השדות, בסדר הצגתם בטופס ובדוח. */
export const SITE_PROFILE_FIELDS: ReadonlyArray<{ key: keyof SiteProfile; label: string }> = [
  { key: 'permitAreaSqm', label: 'שטח האתר לפי היתר (מ״ר)' },
  { key: 'peakWorkerCount', label: 'מספר עובדים צפוי בשיא' },
  { key: 'safetyPlanKind', label: 'סוג תוכנית הבטיחות' },
  { key: 'safetyPlanPreparedBy', label: 'מי הכין את תוכנית הבטיחות' },
  { key: 'safetyPlanUpdatedOn', label: 'מועד עדכון אחרון של התוכנית' },
  { key: 'requiresSection192Notice', label: 'האם האתר מחייב הודעה לפי סעיף 192' },
  { key: 'developerName', label: 'מזמין העבודה' },
  { key: 'mainContractorName', label: 'קבלן ראשי' },
  { key: 'employsForeignWorkers', label: 'האם מועסקים באתר עובדים זרים' },
];

/** תפקידים שהיעדר מינוי רשום עבורם מוצג כ״נדרש בירור״. */
const EXPECTED_ROLES: AppointmentRole[] = ['work_manager', 'safety_inspector'];

export function isActiveOn(appointment: ProfessionalAppointment, onDate: string): boolean {
  return (
    (appointment.startsOn === null || appointment.startsOn <= onDate) &&
    (appointment.endsOn === null || appointment.endsOn >= onDate)
  );
}

export interface ProfileGap {
  key: string;
  label: string;
}

/** מה חסר בתיק האתר ביום נתון — מוצג כ״נדרש בירור״, לא כהפרה. */
export function profileGaps(
  profile: SiteProfile,
  appointments: ProfessionalAppointment[],
  onDate: string,
): ProfileGap[] {
  const gaps: ProfileGap[] = SITE_PROFILE_FIELDS.filter(({ key }) => profile[key] === null).map(
    ({ key, label }) => ({ key, label }),
  );
  for (const role of EXPECTED_ROLES) {
    if (!appointments.some((a) => a.role === role && isActiveOn(a, onDate))) {
      gaps.push({
        key: `appointment:${role}`,
        label: `${APPOINTMENT_ROLE_LABEL[role]} — לא נרשם מינוי`,
      });
    }
  }
  return gaps;
}

/**
 * עובדות לתנאי תחולה. ״unknown״ מחייב הסבר בצ׳קליסט — לא הסתרה.
 *
 * עובדות הביקור (תוכן 0.2): `isFollowUpVisit` — האם היה באתר ביקור קודם שהוגש;
 * `activeStageKinds` — סוגי השלבים שנבחרו בפתיחת הביקור (CQ-07). שתיהן ״נדרש בירור״
 * כברירת מחדל: טופס הפתיחה ממלא אותן כשהמידע בידו.
 */
export interface SiteFacts {
  permitAreaAtLeastSiteManagerThreshold: Tri;
  hasActiveSiteManager: Tri;
  requiresSection192Notice: Tri;
  safetyPlanKind: SafetyPlanKind | 'unknown';
  employsForeignWorkers: Tri;
  isFollowUpVisit: Tri;
  /** ריק = לא נבחרו שלבים = ״נדרש בירור״ (הפרק מופיע, RQ-D06). */
  activeStageKinds: StageKind[] | 'unknown';
}

/** עובדות שמקורן בביקור ולא בתיק האתר. */
export interface VisitFacts {
  isFollowUpVisit?: Tri;
  activeStageKinds?: StageKind[] | 'unknown';
}

const tri = (value: boolean | null): Tri => (value === null ? 'unknown' : value ? 'yes' : 'no');

export function siteFacts(
  profile: SiteProfile,
  appointments: ProfessionalAppointment[],
  onDate: string,
  visit: VisitFacts = {},
): SiteFacts {
  const siteManager = appointments.some((a) => a.role === 'site_manager' && isActiveOn(a, onDate));
  return {
    permitAreaAtLeastSiteManagerThreshold: tri(
      profile.permitAreaSqm === null
        ? null
        : profile.permitAreaSqm >= SITE_MANAGER_AREA_THRESHOLD_SQM,
    ),
    // מינוי רשום = כן. היעדר מינוי רשום ≠ ״אין מנהל אתר״.
    hasActiveSiteManager: siteManager ? 'yes' : 'unknown',
    requiresSection192Notice: tri(profile.requiresSection192Notice),
    safetyPlanKind: profile.safetyPlanKind ?? 'unknown',
    employsForeignWorkers: tri(profile.employsForeignWorkers),
    isFollowUpVisit: visit.isFollowUpVisit ?? 'unknown',
    activeStageKinds: visit.activeStageKinds ?? 'unknown',
  };
}
